/**
 * API Service
 * Handles all API calls to the backend
 */

import axios from 'axios';

// Backend URL comes from one place only — see src/config.js.
import { API_DASHBOARD, API_DB, API_SYNC, REQUEST_TIMEOUT } from '../config';
import { getToken, notifyUnauthorized } from './authAPI';

const apiClient = axios.create({
  baseURL: API_DASHBOARD,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT,
});

const dbClient = axios.create({
  baseURL: API_DB,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT,
});

// ADDED — client for the sync-trigger endpoints (backend/src/routes/syncRoutes.js).
// Separate from apiClient/dbClient only because it points at a different
// base path (/api/sync instead of /api/dashboard or /api/db); no existing
// client is modified.
const syncClient = axios.create({
  baseURL: API_SYNC,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT,
});

/**
 * Optional from/to query params for the report endpoints.
 *
 * Returns undefined rather than {} when no window is set, and omits an empty
 * bound rather than sending it blank. This matters: the backend branches on
 * `if (from || to)` to decide between computing a window live and serving a
 * stored snapshot, and axios serialises an empty string as `?from=` — which is
 * truthy on the server and would silently force every unfiltered request down
 * the compute path with a meaningless bound.
 */
const periodParams = (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return Object.keys(params).length ? params : undefined;
};

/**
 * Auth + error interceptors, applied to EVERY client.
 *
 * WHY THIS IS A LOOP RATHER THAN THREE COPIES: the backend now requires a
 * bearer token on all three base paths. A client that is created later and
 * forgotten here would 401 on every call, so the list below is the single
 * place to register one — add the client to `clients` and it is covered.
 *
 * On 401 the stored session is cleared and an app-wide event is fired, which
 * App.js listens for to return to the login screen. Without this, an expired
 * token would leave the user staring at a dashboard where every panel shows a
 * generic error and nothing explains that they simply need to log in again.
 */
const clients = [apiClient, dbClient, syncClient];

clients.forEach((client) => {
  client.interceptors.request.use((config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        notifyUnauthorized();
      } else {
        console.error('API Error:', error.response?.data || error.message);
      }
      return Promise.reject(error);
    }
  );
});

/**
 * Dashboard API calls
 */
const dashboardAPI = {
  /**
   * Build query params for filters
   */
  buildParams: (params = {}) => {
    const query = {};
    if (params.companyId && params.companyId !== 'all') query.companyId = params.companyId;
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    return query;
  },

  /**
   * Get dashboard summary
   */
  getSummary: async ({ companyId, from, to } = {}) => {
    try {
      const params = dashboardAPI.buildParams({ companyId, from, to });
      const response = await apiClient.get('/summary', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get sales vouchers
   */
  getSalesVouchers: async (companyId, from, to) => {
    try {
      const params = dashboardAPI.buildParams({ companyId, from, to });
      const response = await apiClient.get('/sales', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get purchase vouchers
   */
  getPurchaseVouchers: async (companyId, from, to) => {
    try {
      const params = dashboardAPI.buildParams({ companyId, from, to });
      const response = await apiClient.get('/purchase', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get all ledgers
   */
  getLedgers: async () => {
    try {
      const response = await apiClient.get('/ledgers');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get company summary from new DB API
   */
  getCompanySummary: async (companyId) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/summary`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get outstanding receivables
   */
  getReceivables: async (companyId) => {
    try {
      const params = dashboardAPI.buildParams({ companyId });
      const response = await apiClient.get('/receivables', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get outstanding payables
   */
  getPayables: async (companyId) => {
    try {
      const params = dashboardAPI.buildParams({ companyId });
      const response = await apiClient.get('/payables', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get recent transactions
   */
  getRecentTransactions: async (limit = 10, companyId, from, to) => {
    try {
      const params = dashboardAPI.buildParams({ companyId, from, to });
      params.limit = limit;
      const response = await apiClient.get('/transactions', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get receipt vouchers by company
   */
  getReceipts: async (companyId, limit = 1000, offset = 0) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/receipts`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get payment vouchers by company
   */
  getPayments: async (companyId, limit = 1000, offset = 0) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/payments`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ADDED — get sales vouchers for a single company from the /db API
   * (GET /db/companies/:id/sales). Complements the existing getReceipts /
   * getPayments so the dashboard can pull every voucher type the same way.
   */
  getSalesByCompany: async (companyId, limit = 1000, offset = 0) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/sales`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ADDED — get purchase vouchers for a single company from the /db API
   * (GET /db/companies/:id/purchases).
   */
  getPurchasesByCompany: async (companyId, limit = 1000, offset = 0) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/purchases`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ADDED — get journal vouchers for a single company from the /db API
   * (GET /db/companies/:id/journal-vouchers).
   */
  getJournalVouchersByCompany: async (companyId, limit = 1000, offset = 0) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/journal-vouchers`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ADDED — get ledger master list (with balances) for a single company
   * (GET /db/companies/:id/ledgers).
   */
  getLedgersByCompany: async (companyId) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/ledgers`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getInventory: async (companyId, limit = 1000, offset = 0) => {
  try {
    const response = await dbClient.get(
      `/companies/${companyId}/inventory`,
      {
        params: { limit, offset }
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
},
  /**
 * Get ledger entries by company
 */
getLedgerEntries: async (
  companyId,
  limit = 1000,
  offset = 0,
  filters = {}
) => {
  try {
    const response = await dbClient.get(
      `/companies/${companyId}/ledger-entries`,
      {
        params: {
          limit,
          offset,
          ...filters, // optional filters like from, to, voucherType, ledgerName
        },
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
},
  /**
   * ADDED — get trial balance snapshot for a single company
   * (GET /db/companies/:id/trial-balance).
   */
  // `from`/`to` are optional. Sending them makes the backend COMPUTE the trial
  // balance live for exactly that window instead of serving a stored snapshot;
  // omitting them keeps the previous behaviour untouched. See
  // databaseController.getTrialBalance — the two paths are already there, the
  // UI simply never used the windowed one.
  getTrialBalanceByCompany: async (companyId, from, to) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/trial-balance`, {
        params: periodParams(from, to),
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Trial balance drill-down: groups under one primary group
   * (GET /db/companies/:id/trial-balance/groups/:primary).
   */
  getTrialBalanceGroups: async (companyId, primaryGroup, from, to) => {
    try {
      const response = await dbClient.get(
        `/companies/${companyId}/trial-balance/groups/${encodeURIComponent(primaryGroup)}`,
        { params: periodParams(from, to) }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Trial balance drill-down: rows under one group
   * (GET /db/companies/:id/trial-balance/group/:group).
   */
  getTrialBalanceByGroup: async (companyId, group, from, to) => {
    try {
      const response = await dbClient.get(
        `/companies/${companyId}/trial-balance/group/${encodeURIComponent(group)}`,
        { params: periodParams(from, to) }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ADDED — drill-down level 1: the complete Tally Ledger view for one ledger
   * (GET /db/companies/:id/ledger-statement). Opening balance, every posting
   * with its contra "Particulars" ledger and a running balance, then closing.
   * Accepts ledgerGuid (what trial balance rows carry) or ledgerName.
   */
  getLedgerStatement: async (companyId, { ledgerGuid, ledgerName, from, to, limit = 500, offset = 0 } = {}) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/ledger-statement`, {
        params: { ledgerGuid, ledgerName, from, to, limit, offset },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ADDED — drill-down level 2: one voucher rebuilt exactly as Tally shows it
   * (GET /db/companies/:id/voucher/:guid) — header, every ledger line with its
   * debit/credit split, party, narration, bill allocations and inventory rows.
   */
  getVoucherDetail: async (companyId, guid) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}/voucher/${encodeURIComponent(guid)}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get companies list
   */
  getCompanies: async () => {
    try {
      const response = await dbClient.get('/companies');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get company details
   */
  getCompanyDetails: async (companyId) => {
    try {
      const response = await dbClient.get(`/companies/${companyId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ADDED — start a background sync (POST /api/sync/run-async). Returns
   * immediately with { status: 'running' } instead of blocking for minutes
   * like the existing POST /api/sync/run (which is untouched and still used
   * nowhere here, still works exactly as before for anything else calling it).
   */
  triggerSync: async () => {
    try {
      const response = await syncClient.post('/run-async');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ADDED — poll current sync job status (GET /api/sync/status).
   */
  getSyncStatus: async () => {
    try {
      const response = await syncClient.get('/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default dashboardAPI;
